import React from 'react';
import Card from '../ui/Card';
import { DatabaseIcon } from 'lucide-react';
const DataModelPage: React.FC = () => {
  return <div data-id="element-10353">
      {/* Page Header */}
      <div className="mb-8" data-id="element-10354">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-8 text-white" data-id="element-10355">
          <div className="flex items-center mb-4" data-id="element-10356">
            <DatabaseIcon size={48} className="mr-4" data-id="element-10357" />
            <div data-id="element-10358">
              <h1 className="text-4xl font-bold" data-id="element-10359">Enterprise Data Model</h1>
              <p className="text-purple-100 mt-2 text-lg" data-id="element-10360">
                This page describes the core dimensions, fact tables, and
                relationships used in the analytics platform.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Introduction Card */}
      <Card className="p-6 mb-8 bg-gradient-to-br from-purple-50 to-white border-2 border-purple-100" data-id="element-10361">
        <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-10362">
          About This Data Model
        </h2>
        <p className="text-sm text-gray-700 mb-4" data-id="element-10363">
          This page describes the core dimensions, fact tables, and
          relationships used in the analytics platform.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-10364">
          <div className="bg-white rounded-lg p-4 border border-purple-200" data-id="element-10365">
            <div className="text-2xl font-bold text-purple-600 mb-1" data-id="element-10366">12+</div>
            <div className="text-xs text-gray-600" data-id="element-10367">Core Dimensions</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-200" data-id="element-10368">
            <div className="text-2xl font-bold text-purple-600 mb-1" data-id="element-10369">30+</div>
            <div className="text-xs text-gray-600" data-id="element-10370">Fact Tables</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-purple-200" data-id="element-10371">
            <div className="text-2xl font-bold text-purple-600 mb-1" data-id="element-10372">4+</div>
            <div className="text-xs text-gray-600" data-id="element-10373">Bridge Tables</div>
          </div>
        </div>
      </Card>
      {/* Core Dimensions Section */}
      <Card className="p-6 mb-8 border-2 border-blue-200" data-id="element-10374">
        <div className="flex items-center mb-4" data-id="element-10375">
          <div className="bg-blue-100 p-3 rounded-lg mr-3" data-id="element-10376">
            <span className="text-2xl" data-id="element-10377">🔷</span>
          </div>
          <div data-id="element-10378">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-10379">Core Dimensions</h2>
            <p className="text-sm text-gray-600" data-id="element-10380">
              Master data entities that provide context for all fact tables
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 space-y-4" data-id="element-10381">
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10382">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10383">DIM_PRODUCT</h4>
            <p className="text-sm text-gray-700 mb-2" data-id="element-10384">
              <strong data-id="element-10385">Grain:</strong> 1 row per SKU/component
            </p>
            <p className="text-sm text-gray-700" data-id="element-10386">
              <strong data-id="element-10387">Attributes:</strong> product_id, sku_code, product_name,
              product_line (Lighting/Electrical/Polymer/Metallic/EV), category,
              UOM, standard_cost, launch_date, EOL_flag
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10388">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10389">DIM_CUSTOMER</h4>
            <p className="text-sm text-gray-700 mb-2" data-id="element-10390">
              <strong data-id="element-10391">Grain:</strong> 1 per OEM or aftermarket customer
            </p>
            <p className="text-sm text-gray-700" data-id="element-10392">
              <strong data-id="element-10393">Attributes:</strong> customer_id, customer_name, channel
              (OEM/Aftermarket), segment (2W/3W/4W), region_id, payment_terms,
              GST_number
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10394">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10395">DIM_DEALER</h4>
            <p className="text-sm text-gray-700 mb-2" data-id="element-10396">
              <strong data-id="element-10397">Grain:</strong> 1 per aftermarket dealer/distributor
            </p>
            <p className="text-sm text-gray-700" data-id="element-10398">
              <strong data-id="element-10399">Attributes:</strong> dealer_id, dealer_name,
              linked_distributor, state_id, outlet_type, territory_manager
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10400">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10401">DIM_SUPPLIER</h4>
            <p className="text-sm text-gray-700 mb-2" data-id="element-10402">
              <strong data-id="element-10403">Grain:</strong> 1 per supplier
            </p>
            <p className="text-sm text-gray-700" data-id="element-10404">
              <strong data-id="element-10405">Attributes:</strong> supplier_id, supplier_name, category,
              lead_time_days, vendor_rating
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10406">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10407">DIM_PLANT</h4>
            <p className="text-sm text-gray-700 mb-2" data-id="element-10408">
              <strong data-id="element-10409">Grain:</strong> 1 per manufacturing plant
            </p>
            <p className="text-sm text-gray-700" data-id="element-10410">
              <strong data-id="element-10411">Attributes:</strong> plant_id, plant_name, location,
              capabilities (molding, lighting, electronics), zone
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10412">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10413">
              DIM_PRODUCTION_LINE
            </h4>
            <p className="text-sm text-gray-700 mb-2" data-id="element-10414">
              <strong data-id="element-10415">Grain:</strong> 1 per line
            </p>
            <p className="text-sm text-gray-700" data-id="element-10416">
              <strong data-id="element-10417">Attributes:</strong> line_id, plant_id, line_name,
              line_type, rated_capacity, ideal_cycle_time
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10418">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10419">
              DIM_REGION / DIM_STATE
            </h4>
            <p className="text-sm text-gray-700" data-id="element-10420">
              <strong data-id="element-10421">Attributes:</strong> state_id, state_name, zone
              (North/West/South/East), country
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10422">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10423">DIM_EMPLOYEE</h4>
            <p className="text-sm text-gray-700" data-id="element-10424">
              <strong data-id="element-10425">Attributes:</strong> employee_id, name, role (FTS,
              Auditor, QA, etc.), home_plant, region
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10426">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10427">DIM_FAILURE_MODE</h4>
            <p className="text-sm text-gray-700" data-id="element-10428">
              <strong data-id="element-10429">Attributes:</strong> failure_mode_id, failure_category
              (electrical, mechanical, LED, connector, etc.), severity
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10430">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10431">DIM_CAMPAIGN</h4>
            <p className="text-sm text-gray-700" data-id="element-10432">
              <strong data-id="element-10433">Attributes:</strong> campaign_id, name, channel
              (ATL/BTL/Digital/Trade), start_date, end_date, planned_budget
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10434">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10435">DIM_AUDIT_TYPE</h4>
            <p className="text-sm text-gray-700" data-id="element-10436">
              <strong data-id="element-10437">Attributes:</strong> audit_type_id, type (Internal,
              External OEM, Safety, 5S, Certification)
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4" data-id="element-10438">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-10439">DIM_DATE</h4>
            <p className="text-sm text-gray-700" data-id="element-10440">
              <strong data-id="element-10441">Attributes:</strong> date_id, date, day, week, month,
              quarter, year, fiscal flags
            </p>
          </div>
        </div>
      </Card>
      {/* Fact Tables Section */}
      <Card className="p-6 mb-8 border-2 border-green-200" data-id="element-10442">
        <div className="flex items-center mb-4" data-id="element-10443">
          <div className="bg-green-100 p-3 rounded-lg mr-3" data-id="element-10444">
            <span className="text-2xl" data-id="element-10445">📊</span>
          </div>
          <div data-id="element-10446">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-10447">Fact Tables</h2>
            <p className="text-sm text-gray-600" data-id="element-10448">
              Transactional and aggregated data tables containing measures and
              metrics
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 space-y-6" data-id="element-10449">
          {/* Sales / Revenue */}
          <div data-id="element-10450">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10451">
              SALES / REVENUE
            </h3>
            <div className="space-y-3" data-id="element-10452">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10453">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10454">
                  FACT_SALES_INVOICE
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10455">
                  <strong data-id="element-10456">Grain:</strong> one invoice line
                </p>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10457">
                  <strong data-id="element-10458">Keys:</strong> date_id, customer_id, dealer_id,
                  product_id, plant_id, state_id, channel
                </p>
                <p className="text-sm text-gray-700" data-id="element-10459">
                  <strong data-id="element-10460">Measures:</strong> revenue_amount, quantity, discount,
                  net_realization, cogs_amount
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10461">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10462">
                  FACT_SALES_ORDER
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10463">
                  <strong data-id="element-10464">Grain:</strong> one sales order line
                </p>
                <p className="text-sm text-gray-700" data-id="element-10465">
                  <strong data-id="element-10466">Measures:</strong> quantity_ordered, order_value,
                  status, requested_delivery_date
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10467">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10468">FACT_SHIPMENTS</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10469">
                  <strong data-id="element-10470">Grain:</strong> one shipment line
                </p>
                <p className="text-sm text-gray-700" data-id="element-10471">
                  <strong data-id="element-10472">Measures:</strong> quantity_shipped, shipment_value,
                  actual_delivery_date, otif_flag
                </p>
              </div>
            </div>
          </div>
          {/* Marketing & Leads */}
          <div data-id="element-10473">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10474">
              MARKETING & LEADS
            </h3>
            <div className="space-y-3" data-id="element-10475">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10476">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10477">
                  FACT_CAMPAIGN_SPEND
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10478">
                  <strong data-id="element-10479">Grain:</strong> campaign-day or campaign-month
                </p>
                <p className="text-sm text-gray-700" data-id="element-10480">
                  <strong data-id="element-10481">Measures:</strong> spend_amount, impressions, clicks,
                  leads
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10482">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10483">FACT_LEADS</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10484">
                  <strong data-id="element-10485">Grain:</strong> one lead
                </p>
                <p className="text-sm text-gray-700" data-id="element-10486">
                  <strong data-id="element-10487">Measures:</strong> lead_status, converted_flag,
                  associated_revenue (if mapped)
                </p>
              </div>
            </div>
          </div>
          {/* After-Sales & Warranty */}
          <div data-id="element-10488">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10489">
              AFTER-SALES & WARRANTY
            </h3>
            <div className="space-y-3" data-id="element-10490">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10491">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10492">
                  FACT_COMPLAINTS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10493">
                  <strong data-id="element-10494">Grain:</strong> one complaint/ticket
                </p>
                <p className="text-sm text-gray-700" data-id="element-10495">
                  <strong data-id="element-10496">Measures:</strong> timestamps, resolution_time_hours,
                  category, root_cause
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10497">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10498">
                  FACT_WARRANTY_CLAIMS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10499">
                  <strong data-id="element-10500">Grain:</strong> one warranty claim
                </p>
                <p className="text-sm text-gray-700" data-id="element-10501">
                  <strong data-id="element-10502">Measures:</strong> claim_cost, units_affected,
                  repeat_failure_flag
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10503">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10504">
                  FACT_FTS_VISITS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10505">
                  <strong data-id="element-10506">Grain:</strong> one field visit
                </p>
                <p className="text-sm text-gray-700" data-id="element-10507">
                  <strong data-id="element-10508">Measures:</strong> visit_type, issues_resolved,
                  first_visit_resolution_flag
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10509">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10510">
                  FACT_DEALER_SUPPORT_REQUESTS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10511">
                  <strong data-id="element-10512">Grain:</strong> one support request
                </p>
                <p className="text-sm text-gray-700" data-id="element-10513">
                  <strong data-id="element-10514">Measures:</strong> resolution_time, satisfaction_score
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10515">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10516">FACT_RETURNS</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10517">
                  <strong data-id="element-10518">Grain:</strong> one return entry
                </p>
                <p className="text-sm text-gray-700" data-id="element-10519">
                  <strong data-id="element-10520">Measures:</strong> quantity_returned,
                  valid_return_flag, transit_damage_flag, scrap_value,
                  salvage_value
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10521">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10522">FACT_TRAINING</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10523">
                  <strong data-id="element-10524">Grain:</strong> one training event
                </p>
                <p className="text-sm text-gray-700" data-id="element-10525">
                  <strong data-id="element-10526">Measures:</strong> participants_count,
                  satisfaction_score
                </p>
              </div>
            </div>
          </div>
          {/* Finance */}
          <div data-id="element-10527">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10528">FINANCE</h3>
            <div className="space-y-3" data-id="element-10529">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10530">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10531">FACT_GL_PNL</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10532">
                  <strong data-id="element-10533">Grain:</strong> monthly P&L by business line/entity
                </p>
                <p className="text-sm text-gray-700" data-id="element-10534">
                  <strong data-id="element-10535">Measures:</strong> revenue, material_cost,
                  labour_cost, overhead_cost, EBITDA, PAT
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10536">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10537">
                  FACT_WORKING_CAPITAL
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10538">
                  <strong data-id="element-10539">Grain:</strong> monthly snapshot
                </p>
                <p className="text-sm text-gray-700" data-id="element-10540">
                  <strong data-id="element-10541">Measures:</strong> receivables, payables, inventory,
                  DSO, DPO, DIO, CCC
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10542">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10543">
                  FACT_CAPEX_PROJECTS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10544">
                  <strong data-id="element-10545">Grain:</strong> project-period
                </p>
                <p className="text-sm text-gray-700" data-id="element-10546">
                  <strong data-id="element-10547">Measures:</strong> approved_amount, spent_amount, ROI,
                  completion_percent
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10548">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10549">
                  FACT_PLANT_COST
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10550">
                  <strong data-id="element-10551">Grain:</strong> plant-month
                </p>
                <p className="text-sm text-gray-700" data-id="element-10552">
                  <strong data-id="element-10553">Measures:</strong> total_cost, scrap_cost,
                  energy_cost, labour_cost, cost_per_unit
                </p>
              </div>
            </div>
          </div>
          {/* Supply Chain */}
          <div data-id="element-10554">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10555">
              SUPPLY CHAIN
            </h3>
            <div className="space-y-3" data-id="element-10556">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10557">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10558">
                  FACT_PURCHASE_ORDERS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10559">
                  <strong data-id="element-10560">Grain:</strong> PO line
                </p>
                <p className="text-sm text-gray-700" data-id="element-10561">
                  <strong data-id="element-10562">Measures:</strong> order_qty, order_price,
                  order_value, expected_delivery_date
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10563">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10564">FACT_GRN</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10565">
                  <strong data-id="element-10566">Grain:</strong> goods receipt line
                </p>
                <p className="text-sm text-gray-700" data-id="element-10567">
                  <strong data-id="element-10568">Measures:</strong> qty_received, qty_rejected,
                  material_cost
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10569">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10570">
                  FACT_SUPPLIER_DELIVERY
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10571">
                  <strong data-id="element-10572">Grain:</strong> PO-delivery event
                </p>
                <p className="text-sm text-gray-700" data-id="element-10573">
                  <strong data-id="element-10574">Measures:</strong> actual_delivery_date, on_time_flag,
                  lead_time_days, transit_damage_flag
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10575">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10576">
                  FACT_INVENTORY_SNAPSHOT
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10577">
                  <strong data-id="element-10578">Grain:</strong> product-plant-date snapshot
                </p>
                <p className="text-sm text-gray-700" data-id="element-10579">
                  <strong data-id="element-10580">Measures:</strong> quantity_on_hand, inventory_value,
                  age_bucket
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10581">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10582">
                  FACT_IMPORT_EXPORT
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10583">
                  <strong data-id="element-10584">Grain:</strong> product-date snapshot
                </p>
                <p className="text-sm text-gray-700" data-id="element-10585">
                  <strong data-id="element-10586">Measures:</strong> import_value, export_value, fx_rate
                </p>
              </div>
            </div>
          </div>
          {/* Manufacturing & Quality */}
          <div data-id="element-10587">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10588">
              MANUFACTURING & QUALITY
            </h3>
            <div className="space-y-3" data-id="element-10589">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10590">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10591">
                  FACT_PRODUCTION
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10592">
                  <strong data-id="element-10593">Grain:</strong> production batch or hourly log per
                  line
                </p>
                <p className="text-sm text-gray-700" data-id="element-10594">
                  <strong data-id="element-10595">Measures:</strong> units_produced, good_units,
                  rejected_units, reworked_units, runtime_minutes,
                  planned_minutes
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10596">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10597">FACT_DOWNTIME</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10598">
                  <strong data-id="element-10599">Grain:</strong> each downtime event
                </p>
                <p className="text-sm text-gray-700" data-id="element-10600">
                  <strong data-id="element-10601">Measures:</strong> downtime_minutes, cause_category
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10602">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10603">
                  FACT_QUALITY_INSPECTION
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10604">
                  <strong data-id="element-10605">Grain:</strong> each inspection
                </p>
                <p className="text-sm text-gray-700" data-id="element-10606">
                  <strong data-id="element-10607">Measures:</strong> defect_quantity, defect_type
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10608">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10609">FACT_TOOLING</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10610">
                  <strong data-id="element-10611">Grain:</strong> tool usage log
                </p>
                <p className="text-sm text-gray-700" data-id="element-10612">
                  <strong data-id="element-10613">Measures:</strong> hits, wear_level, deviation_flag
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10614">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10615">
                  FACT_ENERGY_CONSUMPTION
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10616">
                  <strong data-id="element-10617">Grain:</strong> plant-day or line-day
                </p>
                <p className="text-sm text-gray-700" data-id="element-10618">
                  <strong data-id="element-10619">Measures:</strong> kwh, energy_cost
                </p>
              </div>
            </div>
          </div>
          {/* Audits & Compliance */}
          <div data-id="element-10620">
            <h3 className="text-lg font-bold text-green-700 mb-3" data-id="element-10621">
              AUDITS & COMPLIANCE
            </h3>
            <div className="space-y-3" data-id="element-10622">
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10623">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10624">FACT_AUDITS</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10625">
                  <strong data-id="element-10626">Grain:</strong> one audit event
                </p>
                <p className="text-sm text-gray-700" data-id="element-10627">
                  <strong data-id="element-10628">Measures:</strong> audit_score, findings_count,
                  major_nc_count, minor_nc_count
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10629">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10630">FACT_NCR</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10631">
                  <strong data-id="element-10632">Grain:</strong> one NCR
                </p>
                <p className="text-sm text-gray-700" data-id="element-10633">
                  <strong data-id="element-10634">Measures:</strong> severity, status, open/close dates
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10635">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10636">FACT_CAPA</h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10637">
                  <strong data-id="element-10638">Grain:</strong> one CAPA action
                </p>
                <p className="text-sm text-gray-700" data-id="element-10639">
                  <strong data-id="element-10640">Measures:</strong> status, open/close dates,
                  delay_flag
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4" data-id="element-10641">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-10642">
                  FACT_SAFETY_INCIDENTS
                </h4>
                <p className="text-sm text-gray-700 mb-1" data-id="element-10643">
                  <strong data-id="element-10644">Grain:</strong> one safety incident
                </p>
                <p className="text-sm text-gray-700" data-id="element-10645">
                  <strong data-id="element-10646">Measures:</strong> severity, lost_time_hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
      {/* Bridge Tables Section */}
      <Card className="p-6 mb-8 border-2 border-orange-200" data-id="element-10647">
        <div className="flex items-center mb-4" data-id="element-10648">
          <div className="bg-orange-100 p-3 rounded-lg mr-3" data-id="element-10649">
            <span className="text-2xl" data-id="element-10650">🔗</span>
          </div>
          <div data-id="element-10651">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-10652">Bridge Tables</h2>
            <p className="text-sm text-gray-600" data-id="element-10653">
              Many-to-many relationship resolvers and complex hierarchies
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 space-y-3" data-id="element-10654">
          <div className="border-l-4 border-orange-500 pl-4" data-id="element-10655">
            <h4 className="font-bold text-gray-900 mb-1" data-id="element-10656">
              BRIDGE_PRODUCT_LINE_SKU
            </h4>
            <p className="text-sm text-gray-700" data-id="element-10657">
              Links product lines to multiple SKUs if needed.
            </p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4" data-id="element-10658">
            <h4 className="font-bold text-gray-900 mb-1" data-id="element-10659">
              BRIDGE_OEM_PROGRAM_SKU
            </h4>
            <p className="text-sm text-gray-700" data-id="element-10660">
              Links RFQ/programs to OEMs and SKUs for pipeline & SOP reporting.
            </p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4" data-id="element-10661">
            <h4 className="font-bold text-gray-900 mb-1" data-id="element-10662">
              BRIDGE_FAILURE_ROOT_CAUSE
            </h4>
            <p className="text-sm text-gray-700" data-id="element-10663">
              Allows mapping multiple root causes to a failure or complaint.
            </p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4" data-id="element-10664">
            <h4 className="font-bold text-gray-900 mb-1" data-id="element-10665">
              BRIDGE_CAMPAIGN_LEAD_CUSTOMER
            </h4>
            <p className="text-sm text-gray-700" data-id="element-10666">
              Supports multi-touch attribution from campaigns to leads to
              customers.
            </p>
          </div>
        </div>
      </Card>
      {/* ERD Structure Section */}
      <Card className="p-6 mb-8 border-2 border-purple-200" data-id="element-10667">
        <div className="flex items-center mb-4" data-id="element-10668">
          <div className="bg-purple-100 p-3 rounded-lg mr-3" data-id="element-10669">
            <span className="text-2xl" data-id="element-10670">🗺️</span>
          </div>
          <div data-id="element-10671">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-10672">
              ERD Structure (Text Overview)
            </h2>
            <p className="text-sm text-gray-600" data-id="element-10673">
              Entity Relationship Diagram documentation in text format
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-6" data-id="element-10674">
          <div className="space-y-4 text-sm text-gray-700 font-mono" data-id="element-10675">
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10676">
              <strong data-id="element-10677">DIM_PRODUCT</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10678">
                <li data-id="element-10679">→ FACT_SALES_INVOICE (by product_id)</li>
                <li data-id="element-10680">→ FACT_PRODUCTION (by product_id)</li>
                <li data-id="element-10681">→ FACT_WARRANTY_CLAIMS (by product_id)</li>
                <li data-id="element-10682">→ FACT_INVENTORY_SNAPSHOT (by product_id)</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10683">
              <strong data-id="element-10684">DIM_CUSTOMER</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10685">
                <li data-id="element-10686">→ FACT_SALES_INVOICE</li>
                <li data-id="element-10687">→ FACT_COMPLAINTS</li>
                <li data-id="element-10688">→ FACT_WARRANTY_CLAIMS</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10689">
              <strong data-id="element-10690">DIM_DEALER</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10691">
                <li data-id="element-10692">→ FACT_DEALER_SUPPORT_REQUESTS</li>
                <li data-id="element-10693">→ FACT_DEALER_BILLING (if present)</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10694">
              <strong data-id="element-10695">DIM_SUPPLIER</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10696">
                <li data-id="element-10697">→ FACT_PURCHASE_ORDERS</li>
                <li data-id="element-10698">→ FACT_SUPPLIER_DELIVERY</li>
                <li data-id="element-10699">→ FACT_GRN</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10700">
              <strong data-id="element-10701">DIM_PLANT</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10702">
                <li data-id="element-10703">→ FACT_PRODUCTION</li>
                <li data-id="element-10704">→ FACT_DOWNTIME</li>
                <li data-id="element-10705">→ FACT_QUALITY_INSPECTION</li>
                <li data-id="element-10706">→ FACT_ENERGY_CONSUMPTION</li>
                <li data-id="element-10707">→ FACT_PLANT_COST</li>
                <li data-id="element-10708">→ FACT_AUDITS</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10709">
              <strong data-id="element-10710">DIM_PRODUCTION_LINE</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10711">
                <li data-id="element-10712">→ FACT_PRODUCTION</li>
                <li data-id="element-10713">→ FACT_DOWNTIME</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10714">
              <strong data-id="element-10715">DIM_EMPLOYEE</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10716">
                <li data-id="element-10717">→ FACT_FTS_VISITS</li>
                <li data-id="element-10718">→ FACT_AUDITS</li>
                <li data-id="element-10719">→ FACT_CAPA</li>
                <li data-id="element-10720">→ FACT_TRAINING</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10721">
              <strong data-id="element-10722">DIM_FAILURE_MODE</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10723">
                <li data-id="element-10724">→ FACT_WARRANTY_CLAIMS</li>
                <li data-id="element-10725">→ FACT_COMPLAINTS</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10726">
              <strong data-id="element-10727">DIM_CAMPAIGN</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10728">
                <li data-id="element-10729">→ FACT_CAMPAIGN_SPEND</li>
                <li data-id="element-10730">→ FACT_LEADS</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10731">
              <strong data-id="element-10732">DIM_AUDIT_TYPE</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10733">
                <li data-id="element-10734">→ FACT_AUDITS</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-500 pl-4" data-id="element-10735">
              <strong data-id="element-10736">DIM_DATE</strong> joins to:
              <ul className="ml-4 mt-2 space-y-1" data-id="element-10737">
                <li data-id="element-10738">
                  → ALL fact tables on date_id (transaction date, production
                  date, audit date, etc.)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
      {/* Data Governance Section */}
      <Card className="p-6 bg-gradient-to-br from-gray-50 to-white" data-id="element-10739">
        <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-10740">
          Data Governance & Maintenance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10741">
          <div data-id="element-10742">
            <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-10743">
              Update Frequency:
            </h4>
            <ul className="text-sm text-gray-600 space-y-1" data-id="element-10744">
              <li data-id="element-10745">• Dimensions: Updated as master data changes</li>
              <li data-id="element-10746">• Fact Tables: Real-time to daily batch updates</li>
              <li data-id="element-10747">• Bridge Tables: Updated with dimension changes</li>
            </ul>
          </div>
          <div data-id="element-10748">
            <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-10749">
              Data Quality:
            </h4>
            <ul className="text-sm text-gray-600 space-y-1" data-id="element-10750">
              <li data-id="element-10751">• Automated validation rules applied</li>
              <li data-id="element-10752">• Data profiling performed weekly</li>
              <li data-id="element-10753">• Audit logs maintained for all changes</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-10754">
          <p className="text-sm text-gray-600" data-id="element-10755">
            <strong data-id="element-10756">Document Owner:</strong> BI & Analytics Team | Last updated:{' '}
            {new Date().toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
          </p>
        </div>
      </Card>
    </div>;
};
export default DataModelPage;